import React, { useEffect, useMemo, useRef, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "@/components/ui/select";
import { Plus, Pencil, Trash2, Upload, MapPin, AlertTriangle, Search, Send } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const CAN_WRITE = (role) => ["super_admin", "admin"].includes(role);

export default function Territories() {
  const { user } = useAuth();
  const role = user?.role;
  const canWrite = CAN_WRITE(role);

  const [tab, setTab] = useState("list");
  const [list, setList] = useState([]);
  const [regions, setRegions] = useState([]);
  const [search, setSearch] = useState("");
  const [stateFilter, setStateFilter] = useState("");
  const [districtFilter, setDistrictFilter] = useState("");
  const [showInactive, setShowInactive] = useState(false);
  const [unassigned, setUnassigned] = useState(null);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const fileRef = useRef(null);

  // Bulk-assign state (admin/super_admin only)
  const [baOpen, setBaOpen] = useState(false);
  const [baFilters, setBaFilters] = useState({ state: "", district: "", region_id: "", search: "" });
  const [baSelected, setBaSelected] = useState(new Set());
  const [baTargetId, setBaTargetId] = useState("");
  const [baBusy, setBaBusy] = useState(false);
  const [allDistributors, setAllDistributors] = useState([]);

  async function load() {
    const params = {};
    if (search) params.search = search;
    if (stateFilter) params.state = stateFilter;
    if (districtFilter) params.district = districtFilter;
    if (!showInactive) params.active = true;
    const { data } = await api.get("/territories", { params });
    setList(data || []);
  }
  async function loadUnassigned() {
    if (!["super_admin", "admin", "management"].includes(role)) return;
    const { data } = await api.get("/territories/unassigned");
    setUnassigned(data);
  }
  useEffect(() => { load(); api.get("/regions").then((r) => setRegions(r.data || [])); /* eslint-disable-next-line */ }, []);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [search, stateFilter, districtFilter, showInactive]);
  useEffect(() => { if (tab === "unassigned") loadUnassigned(); /* eslint-disable-next-line */ }, [tab]);

  const stateOptions = useMemo(() => Array.from(new Set(list.map((t) => t.state).filter(Boolean))).sort(), [list]);
  const districtOptions = useMemo(() => Array.from(new Set(list.map((t) => t.district).filter(Boolean))).sort(), [list]);

  function openNew() { setEditing({ name: "", state: "", district: "", zone: "", region_id: "", code: "", notes: "" }); setOpen(true); }
  function openEdit(t) { setEditing({ ...t }); setOpen(true); }

  // ─── Bulk Assign ────────────────────────────────────────────────────
  async function openBulkAssign() {
    setBaSelected(new Set()); setBaTargetId(""); setBaOpen(true);
    if (allDistributors.length === 0) {
      try { const { data } = await api.get("/distributors"); setAllDistributors(data || []); } catch { /* ignore */ }
    }
  }
  const baCandidates = useMemo(() => {
    const f = baFilters;
    return (allDistributors || []).filter((d) => {
      if (d.territory_id) return false;  // unassigned only
      if (f.state && (d.state || "") !== f.state) return false;
      if (f.district && (d.district || "") !== f.district) return false;
      if (f.region_id && (d.region_id || "") !== f.region_id) return false;
      if (f.search) {
        const q = f.search.toLowerCase();
        if (!(d.name || "").toLowerCase().includes(q) && !(d.area || "").toLowerCase().includes(q)) return false;
      }
      return true;
    });
  }, [allDistributors, baFilters]);
  const baStateOptions = useMemo(() => Array.from(new Set((allDistributors || []).filter((d) => !d.territory_id).map((d) => d.state).filter(Boolean))).sort(), [allDistributors]);
  const baDistrictOptions = useMemo(() => Array.from(new Set((allDistributors || []).filter((d) => !d.territory_id && (!baFilters.state || d.state === baFilters.state)).map((d) => d.district).filter(Boolean))).sort(), [allDistributors, baFilters.state]);
  function toggleBa(id) { const next = new Set(baSelected); next.has(id) ? next.delete(id) : next.add(id); setBaSelected(next); }
  function toggleAllBa() {
    if (baSelected.size === baCandidates.length) setBaSelected(new Set());
    else setBaSelected(new Set(baCandidates.map((d) => d.id)));
  }
  async function runBulkAssign() {
    if (!baTargetId) { toast.error("Select a target territory"); return; }
    if (baSelected.size === 0) { toast.error("Select at least one distributor"); return; }
    const target = list.find((t) => t.id === baTargetId);
    if (!window.confirm(`You are assigning ${baSelected.size} distributor(s) to territory "${target?.name || baTargetId}". Continue?`)) return;
    setBaBusy(true);
    try {
      const { data } = await api.post("/territories/bulk-assign", {
        territory_id: baTargetId,
        distributor_ids: Array.from(baSelected),
      });
      toast.success(`Assigned ${data.assigned} of ${data.assigned + data.failed} to "${data.target.name}"`);
      // Refresh dialog data + unassigned audit panel
      const fresh = await api.get("/distributors");
      setAllDistributors(fresh.data || []);
      setBaSelected(new Set());
      if (tab === "unassigned") loadUnassigned();
      // Auto-close on full success
      if (data.failed === 0) setBaOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk assign failed");
    } finally { setBaBusy(false); }
  }
  // ────────────────────────────────────────────────────────────────────

  async function save() {
    const payload = {
      name: editing.name?.trim(),
      state: editing.state?.trim(),
      district: editing.district?.trim(),
      zone: editing.zone?.trim() || null,
      region_id: editing.region_id || null,
      code: editing.code?.trim() || null,
      notes: editing.notes?.trim() || null,
    };
    try {
      if (editing.id) {
        await api.patch(`/territories/${editing.id}`, payload);
        toast.success("Territory updated");
      } else {
        await api.post("/territories", payload);
        toast.success("Territory created");
      }
      setOpen(false); setEditing(null); load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  }
  async function softDelete(t) {
    if (!window.confirm(`Soft-delete territory "${t.name}"? It will be hidden but kept for history.`)) return;
    try {
      await api.delete(`/territories/${t.id}`);
      toast.success("Territory soft-deleted"); load();
    } catch (e) {
      const detail = e?.response?.data?.detail || "Delete failed";
      if (e?.response?.status === 409 && window.confirm(`${detail}\n\nForce-delete (super_admin only) — this will clear references on linked distributors and soft-delete the territory. Continue?`)) {
        try {
          await api.delete(`/territories/${t.id}?force=true`);
          toast.success("Force-deleted; distributor links cleared"); load();
        } catch (e2) { toast.error(e2?.response?.data?.detail || "Force delete failed"); }
      } else { toast.error(detail); }
    }
  }

  async function runImport(dryRun) {
    const f = fileRef.current?.files?.[0];
    if (!f) { toast.error("Pick an XLSX file first"); return; }
    const fd = new FormData(); fd.append("file", f);
    try {
      const { data } = await api.post(`/territories/import?dry_run=${dryRun}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setImportResult(data);
      if (!dryRun) { toast.success(`Imported ${data.applied} territor${data.applied === 1 ? "y" : "ies"}`); load(); }
    } catch (e) { toast.error(e?.response?.data?.detail || "Import failed"); }
  }

  return (
    <div className="space-y-6" data-testid="territories-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-[26px] font-bold text-slate-900 tracking-tight flex items-center gap-2"><MapPin className="h-6 w-6 text-emerald-600" /> Territories</h1>
          <p className="text-sm text-slate-500 mt-0.5">State → District → Territory · {list.length} active record{list.length === 1 ? "" : "s"}</p>
        </div>
        {canWrite && (
          <div className="flex gap-2">
            <Button variant="outline" onClick={openBulkAssign} data-testid="territories-bulk-assign-btn"><Send className="h-4 w-4 mr-1.5" /> Assign Territory</Button>
            <Button variant="outline" onClick={() => setImportOpen(true)} data-testid="territories-import-btn"><Upload className="h-4 w-4 mr-1.5" /> Bulk Upload</Button>
            <Button onClick={openNew} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="territories-new-btn"><Plus className="h-4 w-4 mr-1.5" /> New Territory</Button>
          </div>
        )}
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="list" data-testid="territories-tab-list">All Territories</TabsTrigger>
          {["super_admin", "admin", "management"].includes(role) && (
            <TabsTrigger value="unassigned" data-testid="territories-tab-unassigned">Unassigned Audit</TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="list" className="mt-4 space-y-3">
          <Card className="p-3 border border-slate-200 shadow-none rounded-xl">
            <div className="flex flex-wrap items-end gap-2">
              <div className="flex-1 min-w-[220px]">
                <div className="relative"><Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by name or code…" className="pl-9 h-9" data-testid="territories-search" />
                </div>
              </div>
              <div><Label className="text-[10.5px] uppercase tracking-wide text-slate-500">State</Label>
                <Select value={stateFilter || "all"} onValueChange={(v) => setStateFilter(v === "all" ? "" : v)}>
                  <SelectTrigger className="h-9 w-[180px]"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="all">All states</SelectItem>{stateOptions.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label className="text-[10.5px] uppercase tracking-wide text-slate-500">District</Label>
                <Select value={districtFilter || "all"} onValueChange={(v) => setDistrictFilter(v === "all" ? "" : v)}>
                  <SelectTrigger className="h-9 w-[180px]"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="all">All districts</SelectItem>{districtOptions.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <Button variant={showInactive ? "default" : "outline"} size="sm" className="h-9" onClick={() => setShowInactive((v) => !v)} data-testid="territories-show-inactive">{showInactive ? "Hide" : "Show"} Inactive</Button>
            </div>
          </Card>

          <Card className="border border-slate-200 shadow-none rounded-xl overflow-hidden" data-testid="territories-list">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-[11px] uppercase tracking-wide text-slate-500"><tr>
                <th className="px-3 py-2 text-left">Name</th>
                <th className="px-3 py-2 text-left">State</th>
                <th className="px-3 py-2 text-left">District</th>
                <th className="px-3 py-2 text-left">Zone</th>
                <th className="px-3 py-2 text-left">Code</th>
                <th className="px-3 py-2 text-left">Status</th>
                {canWrite && <th className="px-3 py-2 text-right">Actions</th>}
              </tr></thead>
              <tbody>
                {list.length === 0 && <tr><td colSpan={canWrite ? 7 : 6} className="px-3 py-8 text-center text-slate-400">No territories found</td></tr>}
                {list.map((t) => (
                  <tr key={t.id} className="border-t border-slate-100 hover:bg-slate-50/60" data-testid={`territory-row-${t.id}`}>
                    <td className="px-3 py-2 font-medium text-slate-900">{t.name}</td>
                    <td className="px-3 py-2 text-slate-600">{t.state}</td>
                    <td className="px-3 py-2 text-slate-600">{t.district}</td>
                    <td className="px-3 py-2 text-slate-500">{t.zone || "—"}</td>
                    <td className="px-3 py-2 font-mono text-[12px] text-slate-500">{t.code || "—"}</td>
                    <td className="px-3 py-2"><Badge variant={t.active ? "default" : "secondary"} className={t.active ? "bg-emerald-100 text-emerald-800 hover:bg-emerald-100" : ""}>{t.active ? "Active" : "Inactive"}</Badge></td>
                    {canWrite && (
                      <td className="px-3 py-2 text-right">
                        <Button variant="ghost" size="sm" onClick={() => openEdit(t)} data-testid={`territory-edit-${t.id}`}><Pencil className="h-3.5 w-3.5" /></Button>
                        {t.active && <Button variant="ghost" size="sm" onClick={() => softDelete(t)} className="text-red-600 hover:text-red-700 hover:bg-red-50" data-testid={`territory-delete-${t.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </TabsContent>

        <TabsContent value="unassigned" className="mt-4 space-y-3" data-testid="territories-unassigned-panel">
          {!unassigned ? <div className="text-slate-400 text-sm">Loading…</div> : (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
              {[
                { key: "distributors_missing_territory", label: "Distributors w/o Territory", badge: "bg-amber-100 text-amber-800", icon: AlertTriangle, items: unassigned.distributors_missing_territory, count: unassigned.distributors_missing_territory_count },
                { key: "invalid_territory_reference", label: "Invalid Territory FK", badge: "bg-red-100 text-red-800", icon: AlertTriangle, items: unassigned.invalid_territory_reference, count: unassigned.invalid_territory_reference_count },
                { key: "territory_without_distributors", label: "Territories w/o Distributors", badge: "bg-slate-100 text-slate-800", icon: MapPin, items: unassigned.territory_without_distributors, count: unassigned.territory_without_distributors_count },
              ].map((bucket) => (
                <Card key={bucket.key} className="border border-slate-200 shadow-none rounded-xl p-3" data-testid={`unassigned-${bucket.key}`}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-[11px] uppercase tracking-wide text-slate-500">{bucket.label}</div>
                    <Badge className={bucket.badge}>{bucket.count}</Badge>
                  </div>
                  <div className="max-h-[420px] overflow-auto text-xs space-y-1.5">
                    {(bucket.items || []).slice(0, 200).map((it, idx) => (
                      <div key={idx} className="border-b border-slate-100 pb-1">
                        <div className="font-medium text-slate-800">{it.name || "—"}</div>
                        <div className="text-slate-500">{it.state ? `${it.state} / ${it.district}` : (it.reason || it.territory_id_orphan)}</div>
                      </div>
                    ))}
                    {bucket.count === 0 && <div className="text-slate-400 italic py-2">All clean</div>}
                  </div>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Add/Edit dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="territory-dialog">
          <DialogHeader><DialogTitle>{editing?.id ? "Edit Territory" : "New Territory"}</DialogTitle></DialogHeader>
          {editing && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="sm:col-span-2"><Label>Name *</Label>
                <Input value={editing.name || ""} onChange={(e) => setEditing({ ...editing, name: e.target.value })} data-testid="territory-input-name" /></div>
              <div><Label>State *</Label>
                <Input value={editing.state || ""} onChange={(e) => setEditing({ ...editing, state: e.target.value })} data-testid="territory-input-state" /></div>
              <div><Label>District *</Label>
                <Input value={editing.district || ""} onChange={(e) => setEditing({ ...editing, district: e.target.value })} data-testid="territory-input-district" /></div>
              <div><Label>Zone <span className="text-[10px] text-slate-400">(optional)</span></Label>
                <Input value={editing.zone || ""} onChange={(e) => setEditing({ ...editing, zone: e.target.value })} data-testid="territory-input-zone" /></div>
              <div><Label>Region <span className="text-[10px] text-slate-400">(optional)</span></Label>
                <Select value={editing.region_id || "none"} onValueChange={(v) => setEditing({ ...editing, region_id: v === "none" ? "" : v })}>
                  <SelectTrigger data-testid="territory-input-region"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="none">— None —</SelectItem>{regions.map((r) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="sm:col-span-2"><Label>Code <span className="text-[10px] text-slate-400">(blank = auto-generate, e.g. TG-HYD-A13F2C)</span></Label>
                <Input value={editing.code || ""} onChange={(e) => setEditing({ ...editing, code: e.target.value })} data-testid="territory-input-code" /></div>
              <div className="sm:col-span-2"><Label>Notes</Label>
                <Input value={editing.notes || ""} onChange={(e) => setEditing({ ...editing, notes: e.target.value })} data-testid="territory-input-notes" /></div>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="territory-save-btn">{editing?.id ? "Update" : "Create"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk import dialog */}
      <Dialog open={importOpen} onOpenChange={(v) => { setImportOpen(v); if (!v) setImportResult(null); }}>
        <DialogContent data-testid="territory-import-dialog">
          <DialogHeader><DialogTitle>Bulk Upload Territories</DialogTitle></DialogHeader>
          <div className="space-y-3 text-sm">
            <p className="text-slate-600">Required columns: <code className="text-[12px]">name, state, district</code>. Optional: <code className="text-[12px]">zone, region_name, code, notes</code>. Upserts on (state, district, name) — re-running same file is safe.</p>
            <Input ref={fileRef} type="file" accept=".xlsx" data-testid="territory-import-file" />
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => runImport(true)} data-testid="territory-import-dryrun">Dry Run</Button>
              <Button onClick={() => runImport(false)} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="territory-import-apply">Apply</Button>
            </div>
            {importResult && (
              <Card className="p-3 border border-slate-200 shadow-none">
                <div className="grid grid-cols-3 gap-2 text-center text-xs mb-2">
                  <div><div className="text-[10px] uppercase text-slate-500">Total</div><div className="text-lg font-semibold">{importResult.rows_total}</div></div>
                  <div><div className="text-[10px] uppercase text-emerald-700">Valid</div><div className="text-lg font-semibold text-emerald-700">{importResult.rows_valid}</div></div>
                  <div><div className="text-[10px] uppercase text-red-700">Invalid</div><div className="text-lg font-semibold text-red-700">{importResult.rows_invalid}</div></div>
                </div>
                {importResult.applied > 0 && <div className="text-[12px] text-emerald-700">Applied: {importResult.applied} rows written.</div>}
                {importResult.errors_xlsx_url && (
                  <a href={`${process.env.REACT_APP_BACKEND_URL}${importResult.errors_xlsx_url}`}
                     target="_blank" rel="noreferrer"
                     className="text-[12px] text-blue-600 hover:underline" data-testid="territory-import-errors-dl">
                    Download error report (XLSX)
                  </a>
                )}
                {(importResult.sample_errors || []).length > 0 && (
                  <div className="mt-2 text-[12px] space-y-1 max-h-[180px] overflow-auto border-t border-slate-100 pt-1">
                    {importResult.sample_errors.map((s, idx) => (
                      <div key={idx} className="text-red-600"><b>row {s.row}</b> — {s.errors.join(", ")} <span className="text-slate-500">({s.name || "?"})</span></div>
                    ))}
                  </div>
                )}
              </Card>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Bulk Assign dialog */}
      <Dialog open={baOpen} onOpenChange={setBaOpen}>
        <DialogContent className="max-w-3xl" data-testid="bulk-assign-dialog">
          <DialogHeader><DialogTitle>Bulk Assign Distributors to Territory</DialogTitle></DialogHeader>
          <div className="space-y-3 text-sm">
            <Card className="p-3 border border-slate-200 shadow-none rounded-xl">
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
                <div><Label className="text-[10.5px] uppercase tracking-wide text-slate-500">State</Label>
                  <Select value={baFilters.state || "all"} onValueChange={(v) => setBaFilters({ ...baFilters, state: v === "all" ? "" : v, district: "" })}>
                    <SelectTrigger className="h-9" data-testid="ba-filter-state"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="all">All states</SelectItem>{baStateOptions.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label className="text-[10.5px] uppercase tracking-wide text-slate-500">District</Label>
                  <Select value={baFilters.district || "all"} onValueChange={(v) => setBaFilters({ ...baFilters, district: v === "all" ? "" : v })}>
                    <SelectTrigger className="h-9" data-testid="ba-filter-district"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="all">All districts</SelectItem>{baDistrictOptions.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label className="text-[10.5px] uppercase tracking-wide text-slate-500">Region</Label>
                  <Select value={baFilters.region_id || "all"} onValueChange={(v) => setBaFilters({ ...baFilters, region_id: v === "all" ? "" : v })}>
                    <SelectTrigger className="h-9" data-testid="ba-filter-region"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="all">All regions</SelectItem>{regions.map((r) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label className="text-[10.5px] uppercase tracking-wide text-slate-500">Search</Label>
                  <Input value={baFilters.search} onChange={(e) => setBaFilters({ ...baFilters, search: e.target.value })} placeholder="Distributor name…" className="h-9" data-testid="ba-filter-search" />
                </div>
              </div>
              <div className="mt-2 text-[11px] text-slate-500">Only unassigned distributors (territory_id = empty) shown · {baCandidates.length} match{baCandidates.length === 1 ? "" : "es"}</div>
            </Card>

            <Card className="border border-slate-200 shadow-none rounded-xl overflow-hidden">
              <div className="max-h-[320px] overflow-auto">
                <table className="w-full text-[12px]" data-testid="ba-distributors-table">
                  <thead className="bg-slate-50 text-[10px] uppercase tracking-wide text-slate-500 sticky top-0">
                    <tr>
                      <th className="px-2 py-1.5 text-left w-8"><input type="checkbox" checked={baCandidates.length > 0 && baSelected.size === baCandidates.length} onChange={toggleAllBa} data-testid="ba-select-all" /></th>
                      <th className="px-2 py-1.5 text-left">Distributor</th>
                      <th className="px-2 py-1.5 text-left">State</th>
                      <th className="px-2 py-1.5 text-left">District</th>
                    </tr>
                  </thead>
                  <tbody>
                    {baCandidates.length === 0 && <tr><td colSpan={4} className="px-2 py-6 text-center text-slate-400">No unassigned distributors match these filters</td></tr>}
                    {baCandidates.map((d) => (
                      <tr key={d.id} className="border-t border-slate-100 hover:bg-slate-50/60" data-testid={`ba-row-${d.id}`}>
                        <td className="px-2 py-1.5"><input type="checkbox" checked={baSelected.has(d.id)} onChange={() => toggleBa(d.id)} data-testid={`ba-check-${d.id}`} /></td>
                        <td className="px-2 py-1.5 font-medium text-slate-800">{d.name}</td>
                        <td className="px-2 py-1.5 text-slate-600">{d.state || "—"}</td>
                        <td className="px-2 py-1.5 text-slate-600">{d.district || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>

            <Card className="p-3 border border-slate-200 shadow-none rounded-xl">
              <Label className="text-[10.5px] uppercase tracking-wide text-slate-500">Target territory</Label>
              <Select value={baTargetId || "none"} onValueChange={(v) => setBaTargetId(v === "none" ? "" : v)}>
                <SelectTrigger className="h-9" data-testid="ba-target-territory"><SelectValue placeholder="Select territory" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— Select —</SelectItem>
                  {list.filter((t) => t.active).map((t) => <SelectItem key={t.id} value={t.id}>{t.name} <span className="text-[10px] text-slate-400 ml-1">({t.state} / {t.district})</span></SelectItem>)}
                </SelectContent>
              </Select>
              <div className="text-[11px] text-slate-500 mt-1">Only active territories shown.</div>
            </Card>
          </div>
          <DialogFooter className="flex items-center justify-between sm:justify-between">
            <div className="text-[12px] text-slate-600" data-testid="ba-summary">{baSelected.size} selected · target: {list.find((t) => t.id === baTargetId)?.name || "—"}</div>
            <div className="flex gap-2">
              <Button variant="ghost" onClick={() => setBaOpen(false)} disabled={baBusy}>Cancel</Button>
              <Button onClick={runBulkAssign} disabled={baBusy || baSelected.size === 0 || !baTargetId} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="ba-assign-btn">{baBusy ? "Assigning…" : `Assign ${baSelected.size}`}</Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
